import type { PresentationEvent } from '../../presentation/events';
import { BASE_MUSIC_TRACKS, FEATURE_MUSIC_TRACK, SAMPLED_CUES, SHUFFLE_AUDIO_ENABLED } from './assets';
import { createSynthCueRecipe, getScatterSoundProfile } from './cue-recipes';
import { DEFAULT_AUDIO_PREFERENCES } from './preferences';
import type {
  AudioCue,
  AudioElementFactory,
  AudioElementLike,
  AudioManagerPort,
  AudioPlaybackOptions,
  AudioPlaylistRandomSource,
  AudioPreferences,
} from './types';

const MUSIC_FADE_MS = 280;
const SYNTH_BOOST = 1.7;

type AudioBus = 'music' | 'effects';

interface MediaLevel {
  readonly bus: AudioBus;
  level: number;
}

interface MediaGraphNode {
  readonly source: MediaElementAudioSourceNode;
  readonly gain: GainNode;
}

interface AudioGraph {
  readonly context: AudioContext;
  readonly master: GainNode;
  readonly music: GainNode;
  readonly effects: GainNode;
  readonly mediaNodes: Map<AudioElementLike, MediaGraphNode>;
}

interface ActiveSynthVoice {
  readonly oscillator: OscillatorNode;
  readonly gain: GainNode;
}

interface ActiveBufferSample {
  readonly source: AudioBufferSourceNode;
  readonly gain: GainNode;
}

type AudioDataLoader = (url: string) => Promise<ArrayBuffer>;

async function loadAudioData(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Audio-Sample konnte nicht geladen werden: ${response.status}`);
  return response.arrayBuffer();
}

class CryptoPlaylistRandomSource implements AudioPlaylistRandomSource {
  nextInt(maxExclusive: number): number {
    if (!Number.isSafeInteger(maxExclusive) || maxExclusive <= 0) throw new RangeError('Ungültige Playlist-Grenze.');
    const maximum = 0x1_0000_0000;
    const limit = maximum - (maximum % maxExclusive);
    const buffer = new Uint32Array(1);
    do {
      globalThis.crypto.getRandomValues(buffer);
    } while (buffer[0]! >= limit);
    return buffer[0]! % maxExclusive;
  }
}

function createBrowserAudio(source: string): AudioElementLike {
  const audio = new Audio(source);
  audio.preload = 'auto';
  return audio;
}

function percent(value: number): number {
  return Math.max(0, Math.min(1, value / 100));
}

export interface AudioManagerOptions {
  readonly elementFactory?: AudioElementFactory;
  readonly playlistRandom?: AudioPlaylistRandomSource;
  readonly initialPreferences?: AudioPreferences;
  readonly contextFactory?: () => AudioContext | null;
  readonly audioDataLoader?: AudioDataLoader;
}

export class AudioManager implements AudioManagerPort {
  private readonly elementFactory: AudioElementFactory;
  private readonly playlistRandom: AudioPlaylistRandomSource;
  private readonly baseTracks: readonly AudioElementLike[];
  private readonly featureTrack: AudioElementLike;
  private readonly pools = new Map<AudioCue, AudioElementLike[]>();
  private readonly poolIndices = new Map<AudioCue, number>();
  private readonly mediaLevels = new Map<AudioElementLike, MediaLevel>();
  private readonly activeSynthVoices = new Set<ActiveSynthVoice>();
  private readonly activeBufferSamples = new Set<ActiveBufferSample>();
  private readonly contextFactory: () => AudioContext | null;
  private readonly scatterSampleData: Promise<ArrayBuffer | null>;
  private scatterSample: AudioBuffer | null = null;
  private scatterSampleDecode: Promise<void> | null = null;
  private currentBaseIndex: number | null = null;
  private currentMusic: AudioElementLike | null = null;
  private context: AudioContext | null = null;
  private graph: AudioGraph | null = null;
  private unlocked = false;
  private destroyed = false;
  private duckFactor = 1;
  private duckTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private fadeToken = 0;
  private mutablePreferences: AudioPreferences;
  private presentationMode: 'cards' | 'performance' = 'cards';
  private turbo = false;

  constructor(options: AudioManagerOptions = {}) {
    this.elementFactory = options.elementFactory ?? createBrowserAudio;
    this.playlistRandom = options.playlistRandom ?? new CryptoPlaylistRandomSource();
    this.contextFactory = options.contextFactory ?? (() => {
      const AudioContextConstructor = globalThis.AudioContext
        ?? (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      return AudioContextConstructor === undefined ? null : new AudioContextConstructor();
    });
    const scatterUrl = SAMPLED_CUES['scatter-1']?.url;
    this.scatterSampleData = scatterUrl === undefined
      ? Promise.resolve(null)
      : (options.audioDataLoader ?? loadAudioData)(scatterUrl).catch(() => null);
    this.mutablePreferences = options.initialPreferences ?? DEFAULT_AUDIO_PREFERENCES;
    this.baseTracks = BASE_MUSIC_TRACKS.map((track, index) => {
      const audio = this.elementFactory(track.url);
      audio.preload = 'auto';
      audio.loop = false;
      audio.addEventListener('ended', () => {
        if (this.currentBaseIndex === index && !this.destroyed) void this.startNextBaseTrack();
      });
      return audio;
    });
    this.featureTrack = this.elementFactory(FEATURE_MUSIC_TRACK.url);
    this.featureTrack.preload = 'auto';
    this.featureTrack.loop = true;
    this.baseTracks.forEach((track) => this.mediaLevels.set(track, { bus: 'music', level: 0 }));
    this.mediaLevels.set(this.featureTrack, { bus: 'music', level: 0 });
    Object.entries(SAMPLED_CUES).forEach(([cue, definition]) => {
      if (definition === undefined) return;
      const channels = Array.from({ length: definition.poolSize }, () => {
        const audio = this.elementFactory(definition.url);
        audio.preload = 'auto';
        this.mediaLevels.set(audio, { bus: 'effects', level: 0 });
        return audio;
      });
      this.pools.set(cue as AudioCue, channels);
    });
    this.applyVolumes();
  }

  get preferences(): AudioPreferences {
    return this.mutablePreferences;
  }

  async unlock(): Promise<void> {
    if (this.destroyed) return;
    try {
      this.context ??= this.contextFactory();
      const context = this.context;
      if (context === null || context.state === 'closed') return;
      this.ensureGraph(context);

      // iOS kann einen bestehenden Context nach App-/Tab-Wechsel in den
      // nicht standardisierten Zustand `interrupted` setzen. Deshalb wird bei
      // jeder echten Nutzerinteraktion erneut resume() versucht.
      const resume = context.state === 'running'
        ? Promise.resolve()
        : context.resume().catch(() => undefined);
      if (!this.unlocked) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        gain.gain.value = 0.00001;
        oscillator.connect(gain).connect(this.graph?.master ?? context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.012);
        this.unlocked = true;
      }
      await resume;
      await this.prepareScatterSample(context);
      if (this.currentMusic !== null && this.currentMusic.paused && this.isMusicAudible()) {
        void this.currentMusic.play().catch(() => undefined);
      }
    } catch {
      // Audio darf den Spielstart auch bei Browser-/Autoplay-Verweigerung nie blockieren.
    }
  }

  attachUnlock(target: Document = document): () => void {
    const unlock = (): void => { void this.unlock(); };
    const resume = (): void => {
      if (target.visibilityState !== 'visible') {
        this.suspend();
        return;
      }
      void this.unlock().then(() => {
        if (this.currentMusic !== null && this.isMusicAudible()) {
          void this.currentMusic.play().catch(() => undefined);
        }
      });
    };
    target.addEventListener('pointerdown', unlock, { passive: true });
    target.addEventListener('touchend', unlock, { passive: true });
    target.addEventListener('keydown', unlock);
    target.addEventListener('visibilitychange', resume);
    return () => {
      target.removeEventListener('pointerdown', unlock);
      target.removeEventListener('touchend', unlock);
      target.removeEventListener('keydown', unlock);
      target.removeEventListener('visibilitychange', resume);
    };
  }

  suspend(): void {
    this.pauseMusic();
    const context = this.context;
    if (context === null || context.state !== 'running') return;
    void context.suspend().catch(() => undefined);
  }

  setPreferences(preferences: AudioPreferences): void {
    this.mutablePreferences = preferences;
    this.applyVolumes();
    if (!this.isMusicAudible()) this.pauseMusic();
    else if (this.currentMusic !== null) void this.currentMusic.play().catch(() => undefined);
    if (!preferences.enabled || preferences.sfxVolume <= 0) {
      this.pauseAllEffects();
      this.stopSynthEffects();
      this.stopBufferEffects();
    }
  }

  emit(event: PresentationEvent): void {
    switch (event.type) {
      case 'spin-start': {
        this.presentationMode = event.mode;
        this.turbo = event.turbo;
        this.playCue('draw', {
          playbackRate: event.turbo ? 1.08 : 1,
          gain: event.turbo ? 0.9 : 0.945,
          turbo: event.turbo,
        });
        return;
      }
      case 'shuffle':
        this.playCue('shuffle', {
          phase: 'clear', turbo: this.turbo, playbackRate: this.turbo ? 1.04 : 0.95,
        });
        return;
      case 'deal-complete':
        if (event.mode === 'cards') {
          this.playCue('shuffle', {
            phase: 'deal', turbo: this.turbo, playbackRate: this.turbo ? 1.12 : 1.02, gain: 0.76,
          });
        }
        return;
      case 'reel-reveal-start':
        this.playCue('card-flip', {
          reelIndex: event.reel,
          playbackRate: this.getCardFlipRate(event.reel),
          gain: this.turbo ? 0.57 : 0.86,
          sample: true,
          turbo: this.turbo,
        });
        return;
      case 'anticipation-start': this.playCue('anticipation', { duckMusicMs: 450 }); return;
      case 'reel-stop': {
        if (this.presentationMode === 'performance') {
          this.playCue('card-flip', {
            reelIndex: event.reel,
            playbackRate: this.getCardFlipRate(event.reel),
            gain: this.turbo ? 0.57 : 0.86,
            sample: false,
            turbo: this.turbo,
          });
        }
        const reelsRemaining = Math.max(0, 4 - event.reel);
        const scatterIsAudible = event.hasScatter
          && (event.scatterStage >= 3 || event.scatterStage + reelsRemaining >= 3);
        if (scatterIsAudible) {
          const stage = Math.max(1, Math.min(3, event.scatterStage)) as 1 | 2 | 3;
          this.playCue(`scatter-${stage}`, { duckMusicMs: stage === 3 ? 560 : 300 });
        } else if (this.presentationMode === 'performance') {
          this.playCue('reel-stop', { reelIndex: event.reel });
        }
        if (event.hasNewWild && !scatterIsAudible) this.playCue('wild-impact', { duckMusicMs: 420 });
        return;
      }
      case 'line-show':
        this.playCue('line-step', { step: event.ordinal, total: event.total });
        return;
      case 'sticky-reveal': return;
      case 'god-activation':
        this.playCue('pot-activate', { duckMusicMs: 760 });
        return;
      case 'pot-highlight':
        this.playCue('pot-activate', { duckMusicMs: 760 });
        return;
      case 'pot-awarded': return;
      case 'pot-flip':
        this.playCue('card-flip', { reelIndex: event.position.reel, sample: true });
        return;
      case 'pot-transformed': this.playCue('wild-impact', { duckMusicMs: 420 }); return;
      case 'retrigger': this.playCue('retrigger', { duckMusicMs: 700 }); return;
      case 'presentation-complete': return;
    }
  }

  playCue(cue: AudioCue, options: AudioPlaybackOptions = {}): void {
    if (this.destroyed || !this.mutablePreferences.enabled || this.mutablePreferences.sfxVolume <= 0) return;
    if (cue === 'shuffle' && !SHUFFLE_AUDIO_ENABLED) return;
    if ((options.duckMusicMs ?? 0) > 0) this.duckMusic(options.duckMusicMs!);
    const definition = SAMPLED_CUES[cue];
    const pool = this.pools.get(cue);
    const bufferedScatterPlayed = options.sample !== false
      && (cue === 'scatter-1' || cue === 'scatter-2' || cue === 'scatter-3')
      && this.playBufferedScatter(cue, options);
    if (!bufferedScatterPlayed && options.sample !== false
      && definition !== undefined && pool !== undefined && pool.length > 0) {
      const nextIndex = this.poolIndices.get(cue) ?? 0;
      const channel = pool.find((entry) => entry.paused) ?? pool[nextIndex % pool.length]!;
      this.poolIndices.set(cue, (nextIndex + 1) % pool.length);
      channel.pause();
      channel.currentTime = 0;
      if (cue === 'scatter-1' || cue === 'scatter-2' || cue === 'scatter-3') {
        channel.preservesPitch = false;
        channel.webkitPreservesPitch = false;
      }
      channel.playbackRate = options.playbackRate ?? this.getSampleRate(cue);
      this.setMediaLevel(channel, definition.gain * (options.gain ?? 1));
      void channel.play().catch(() => undefined);
    }
    this.playSynthCue(cue, options);
  }

  async startBaseMusic(options: { readonly randomStart?: boolean } = {}): Promise<void> {
    if (this.destroyed) return;
    this.featureTrack.pause();
    this.featureTrack.currentTime = 0;
    const nextIndex = this.currentBaseIndex === null
      ? (options.randomStart === true ? this.pickBaseIndex(null) : 0)
      : this.pickBaseIndex(this.currentBaseIndex);
    await this.activateMusic(this.baseTracks[nextIndex]!, nextIndex);
  }

  async startFeatureMusic(): Promise<void> {
    if (this.destroyed) return;
    this.baseTracks.forEach((track) => track.pause());
    this.currentBaseIndex = null;
    this.featureTrack.currentTime = 0;
    await this.activateMusic(this.featureTrack, null);
  }

  async fadeOutMusic(durationMs = MUSIC_FADE_MS): Promise<void> {
    if (this.destroyed) return;
    const token = ++this.fadeToken;
    const tracks = [...this.baseTracks, this.featureTrack];
    const initialLevels = tracks.map((track) => this.mediaLevels.get(track)?.level ?? 0);
    const steps = Math.max(1, Math.round(durationMs / 35));
    for (let step = 1; step <= steps; step += 1) {
      if (token !== this.fadeToken || this.destroyed) return;
      const progress = step / steps;
      tracks.forEach((track, index) => this.setMediaLevel(track, initialLevels[index]! * (1 - progress)));
      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, durationMs / steps));
    }
    if (token !== this.fadeToken || this.destroyed) return;
    tracks.forEach((track) => {
      track.pause();
      track.currentTime = 0;
      this.setMediaLevel(track, 0);
    });
    this.currentMusic = null;
    this.currentBaseIndex = null;
  }

  stopMusic(): void {
    this.fadeToken += 1;
    this.baseTracks.forEach((track) => {
      track.pause();
      track.currentTime = 0;
    });
    this.featureTrack.pause();
    this.featureTrack.currentTime = 0;
    this.baseTracks.forEach((track) => this.setMediaLevel(track, 0));
    this.setMediaLevel(this.featureTrack, 0);
    this.currentMusic = null;
    this.currentBaseIndex = null;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopMusic();
    this.pauseAllEffects();
    this.stopSynthEffects();
    this.stopBufferEffects();
    if (this.duckTimer !== null) globalThis.clearTimeout(this.duckTimer);
    this.duckTimer = null;
    if (this.context !== null) void this.context.close();
    this.disconnectGraph();
    this.context = null;
  }

  private async activateMusic(channel: AudioElementLike, baseIndex: number | null): Promise<void> {
    const previous = this.currentMusic;
    const token = ++this.fadeToken;
    this.currentMusic = channel;
    this.currentBaseIndex = baseIndex;
    this.setMediaLevel(channel, 0);
    if (this.isMusicAudible()) await channel.play().catch(() => undefined);
    const steps = 8;
    for (let step = 1; step <= steps; step += 1) {
      if (token !== this.fadeToken || this.destroyed) return;
      const progress = step / steps;
      this.setMediaLevel(channel, this.getMusicTrackGain(channel) * progress);
      if (previous !== null && previous !== channel) {
        this.setMediaLevel(previous, this.getMusicTrackGain(previous) * (1 - progress));
      }
      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, MUSIC_FADE_MS / steps));
    }
    if (previous !== null && previous !== channel) previous.pause();
  }

  private async startNextBaseTrack(): Promise<void> {
    if (this.currentBaseIndex === null) return;
    const nextIndex = this.pickBaseIndex(this.currentBaseIndex);
    await this.activateMusic(this.baseTracks[nextIndex]!, nextIndex);
  }

  private pickBaseIndex(previous: number | null): number {
    if (previous === null) return this.playlistRandom.nextInt(this.baseTracks.length);
    const choices = this.baseTracks.map((_, index) => index).filter((index) => index !== previous);
    return choices[this.playlistRandom.nextInt(choices.length)]!;
  }

  private getMusicTrackGain(channel: AudioElementLike): number {
    const definition = channel === this.featureTrack
      ? FEATURE_MUSIC_TRACK
      : BASE_MUSIC_TRACKS[this.baseTracks.indexOf(channel)] ?? BASE_MUSIC_TRACKS[0];
    return definition.gain;
  }

  private applyVolumes(): void {
    const graph = this.graph;
    if (graph !== null) {
      const enabled = this.mutablePreferences.enabled;
      graph.master.gain.setValueAtTime(enabled ? percent(this.mutablePreferences.masterVolume) : 0, graph.context.currentTime);
      graph.music.gain.setValueAtTime(percent(this.mutablePreferences.musicVolume) * this.duckFactor, graph.context.currentTime);
      graph.effects.gain.setValueAtTime(percent(this.mutablePreferences.sfxVolume), graph.context.currentTime);
    }
    for (const [channel, level] of this.mediaLevels) this.applyMediaLevel(channel, level);
  }

  private getSampleRate(cue: AudioCue): number {
    if (cue === 'scatter-1' || cue === 'scatter-2' || cue === 'scatter-3') {
      return getScatterSoundProfile(Number(cue.at(-1))).playbackRate;
    }
    return 1;
  }

  private getCardFlipRate(reel: number): number {
    return (this.turbo
      ? [0.9, 0.96, 1.01, 1.06, 1.1]
      : [0.84, 0.9, 0.96, 1.02, 1.08])[reel]! * 0.95;
  }

  private duckMusic(durationMs: number): void {
    if (this.duckTimer !== null) globalThis.clearTimeout(this.duckTimer);
    this.duckFactor = 0.52;
    this.applyVolumes();
    this.duckTimer = globalThis.setTimeout(() => {
      this.duckTimer = null;
      this.duckFactor = 1;
      this.applyVolumes();
    }, durationMs);
  }

  private pauseAllEffects(): void {
    this.pools.forEach((pool) => pool.forEach((channel) => {
      channel.pause();
      channel.currentTime = 0;
      this.setMediaLevel(channel, 0);
    }));
  }

  private pauseMusic(): void {
    this.baseTracks.forEach((track) => track.pause());
    this.featureTrack.pause();
  }

  private isMusicAudible(): boolean {
    return this.mutablePreferences.enabled
      && this.mutablePreferences.masterVolume > 0
      && this.mutablePreferences.musicVolume > 0;
  }

  private stopSynthEffects(): void {
    const stopAt = this.context?.currentTime ?? 0;
    for (const voice of this.activeSynthVoices) {
      voice.oscillator.onended = null;
      try { voice.oscillator.stop(stopAt); } catch { /* Bereits beendet. */ }
      try { voice.oscillator.disconnect(); } catch { /* Bereits getrennt. */ }
      try { voice.gain.disconnect(); } catch { /* Bereits getrennt. */ }
    }
    this.activeSynthVoices.clear();
  }

  private stopBufferEffects(): void {
    const stopAt = this.context?.currentTime ?? 0;
    for (const sample of this.activeBufferSamples) {
      sample.source.onended = null;
      try { sample.source.stop(stopAt); } catch { /* Bereits beendet. */ }
      try { sample.source.disconnect(); } catch { /* Bereits getrennt. */ }
      try { sample.gain.disconnect(); } catch { /* Bereits getrennt. */ }
    }
    this.activeBufferSamples.clear();
  }

  private async prepareScatterSample(context: AudioContext): Promise<void> {
    if (this.scatterSample !== null) return;
    if (this.scatterSampleDecode !== null) return this.scatterSampleDecode;
    if (typeof context.decodeAudioData !== 'function') return;
    this.scatterSampleDecode = this.scatterSampleData
      .then((data) => data === null ? null : context.decodeAudioData(data.slice(0)))
      .then((buffer) => {
        if (buffer !== null && this.context === context && context.state !== 'closed') {
          this.scatterSample = buffer;
        }
      })
      .catch(() => undefined)
      .finally(() => { this.scatterSampleDecode = null; });
    return this.scatterSampleDecode;
  }

  private playBufferedScatter(
    cue: 'scatter-1' | 'scatter-2' | 'scatter-3',
    options: AudioPlaybackOptions,
  ): boolean {
    const context = this.context;
    const buffer = this.scatterSample;
    if (context === null || context.state !== 'running' || buffer === null
      || typeof context.createBufferSource !== 'function') return false;
    try {
      const profile = getScatterSoundProfile(Number(cue.at(-1)));
      const definition = SAMPLED_CUES[cue];
      if (definition === undefined) return false;
      const now = context.currentTime;
      const playbackRate = options.playbackRate ?? profile.playbackRate;
      const duration = Math.max(0.12, buffer.duration / Math.max(0.01, playbackRate));
      const releaseAt = now + duration;
      const source = context.createBufferSource();
      const gain = context.createGain();
      const sample: ActiveBufferSample = { source, gain };
      const fallbackMix = this.graph === null
        ? percent(this.mutablePreferences.masterVolume) * percent(this.mutablePreferences.sfxVolume)
        : 1;
      const peak = Math.max(0.0001, definition.gain * (options.gain ?? 1) * fallbackMix);
      source.buffer = buffer;
      source.playbackRate.setValueAtTime(playbackRate, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(peak, now + 0.012);
      gain.gain.setValueAtTime(Math.max(0.0001, peak * 0.78), now + Math.max(0.08, duration - 0.16));
      gain.gain.exponentialRampToValueAtTime(0.0001, releaseAt);
      source.connect(gain).connect(this.graph?.effects ?? context.destination);
      source.onended = () => {
        if (!this.activeBufferSamples.delete(sample)) return;
        try { source.disconnect(); } catch { /* Bereits getrennt. */ }
        try { gain.disconnect(); } catch { /* Bereits getrennt. */ }
      };
      this.activeBufferSamples.add(sample);
      source.start(now);
      source.stop(releaseAt + 0.02);
      return true;
    } catch {
      return false;
    }
  }

  private playSynthCue(cue: AudioCue, options: AudioPlaybackOptions): void {
    if (this.context === null || this.context.state !== 'running') return;
    const tones = createSynthCueRecipe(cue, options);
    const startAt = this.context.currentTime;
    const fallbackMix = this.graph === null
      ? percent(this.mutablePreferences.masterVolume) * percent(this.mutablePreferences.sfxVolume)
      : 1;
    const mix = fallbackMix * (options.gain ?? 1) * SYNTH_BOOST;
    for (const tone of tones) {
      const start = startAt + (tone.delaySeconds ?? 0);
      const end = start + tone.durationSeconds;
      const attack = Math.max(0.001, tone.attackSeconds ?? 0.01);
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      const voice: ActiveSynthVoice = { oscillator, gain };
      this.activeSynthVoices.add(voice);
      oscillator.type = tone.waveform;
      oscillator.frequency.setValueAtTime(tone.fromHz, start);
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, tone.toHz), end);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, tone.gain * mix), start + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.connect(gain).connect(this.graph?.effects ?? this.context.destination);
      oscillator.onended = () => {
        if (!this.activeSynthVoices.delete(voice)) return;
        try { oscillator.disconnect(); } catch { /* Bereits getrennt. */ }
        try { gain.disconnect(); } catch { /* Bereits getrennt. */ }
      };
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  }

  private setMediaLevel(channel: AudioElementLike, level: number): void {
    const state = this.mediaLevels.get(channel);
    if (state === undefined) return;
    state.level = Math.max(0, Math.min(1, level));
    this.applyMediaLevel(channel, state);
  }

  private applyMediaLevel(channel: AudioElementLike, state: MediaLevel): void {
    const node = this.graph?.mediaNodes.get(channel);
    if (node !== undefined && this.graph !== null) {
      channel.volume = 1;
      node.gain.gain.setValueAtTime(state.level, this.graph.context.currentTime);
      return;
    }
    const enabled = this.mutablePreferences.enabled;
    const busLevel = state.bus === 'music'
      ? percent(this.mutablePreferences.musicVolume) * this.duckFactor
      : percent(this.mutablePreferences.sfxVolume);
    channel.volume = enabled
      ? Math.min(1, state.level * percent(this.mutablePreferences.masterVolume) * busLevel)
      : 0;
  }

  private ensureGraph(context: AudioContext): void {
    if (this.graph?.context === context || typeof context.createMediaElementSource !== 'function') return;
    if (this.graph !== null) return;

    const master = context.createGain();
    const music = context.createGain();
    const effects = context.createGain();
    music.connect(master);
    effects.connect(master);
    master.connect(context.destination);
    const graph: AudioGraph = { context, master, music, effects, mediaNodes: new Map() };

    for (const [channel, state] of this.mediaLevels) {
      try {
        const source = context.createMediaElementSource(channel as unknown as HTMLMediaElement);
        const gain = context.createGain();
        source.connect(gain).connect(state.bus === 'music' ? music : effects);
        graph.mediaNodes.set(channel, { source, gain });
        channel.volume = 1;
      } catch {
        // Einzelne Media-Elemente dürfen auf restriktiven Browsern im nativen
        // Fallback bleiben; alle übrigen Quellen nutzen weiterhin den Graphen.
      }
    }

    if (graph.mediaNodes.size === 0) {
      try { music.disconnect(); } catch { /* Bereits getrennt. */ }
      try { effects.disconnect(); } catch { /* Bereits getrennt. */ }
      try { master.disconnect(); } catch { /* Bereits getrennt. */ }
      return;
    }
    this.graph = graph;
    this.applyVolumes();
  }

  private disconnectGraph(): void {
    const graph = this.graph;
    this.graph = null;
    if (graph === null) return;
    for (const node of graph.mediaNodes.values()) {
      try { node.source.disconnect(); } catch { /* Bereits getrennt. */ }
      try { node.gain.disconnect(); } catch { /* Bereits getrennt. */ }
    }
    for (const bus of [graph.music, graph.effects, graph.master]) {
      try { bus.disconnect(); } catch { /* Bereits getrennt. */ }
    }
  }
}
